// S1 (docs/27 §4 item 3): pre-boot action queue.
//
// With the desk view server-rendered, controls are visible and clickable
// before the legacy runtime chunk has booted. Semantic actions dispatched in
// that window reach the legacyActions choke point, which routes them here:
// they are queued and replayed in order once the legacy bootstrap completes.
// Transient hover/pointer feedback is dropped instead of replayed — replaying
// a hover after the fact is meaningless.
//
// The queue is a client-only module singleton; on the server nothing
// dispatches actions (render is pure), so it stays empty.

import type { LegacyHandlerName } from '@/msgdeskRuntime/contracts/legacyEvents'

interface QueuedAction {
  name: string
  replay: () => unknown
}

// Transient feedback events: dropped, never replayed.
const DROPPED_PRE_BOOT_EVENTS = new Set<LegacyHandlerName | string>([
  'mev_msg_menu_li_over_evm',
  'mev_msg_sub_over_evm',
  'mev_msg_help_over_evm',
  'mev_msg_email_button_evm',
])

let legacyCommandLive = false
let queue: QueuedAction[] = []

export function isLegacyCommandLive(): boolean {
  return legacyCommandLive
}

export function shouldDropPreBootEvent(name: string): boolean {
  return DROPPED_PRE_BOOT_EVENTS.has(name)
}

/** Queue an action dispatched before the legacy runtime was live. */
export function enqueuePreBootAction(name: string, replay: () => unknown): void {
  if (shouldDropPreBootEvent(name)) return
  queue.push({ name, replay })
}

/**
 * Mark the legacy runtime live and replay the queued actions in dispatch
 * order. Called once by the app host after the legacy bootstrap completes.
 */
export function markLegacyCommandLive(): void {
  legacyCommandLive = true
  const pending = queue
  queue = []
  for (const action of pending) {
    try {
      action.replay()
    } catch (error) {
      console.warn('[msgdesk pre-boot queue] replay failed:', action.name, error)
    }
  }
}

/** Test/HMR helper: reset the live flag and drop any queued actions. */
export function resetPreBootQueue(): void {
  legacyCommandLive = false
  queue = []
}
